import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { applicationService } from "../services/applicationService";
import interviewService from "../services/interviewService";
import CandidateHistory from "../components/CandidateHistory";
import InterviewReview, { VideoThumb } from "../components/InterviewReview";
import ScreeningQuestions from "../components/ScreeningQuestions";

// The candidate, on one page.
//
// ─── WHAT THIS REPLACED, AND WHY ────────────────────────────────────────────
//
// A 1,759-line side drawer. A drawer is the wrong container for this: it opens
// over the list it was launched from, so it is narrow, it cannot be linked to,
// and everything it holds — resume analysis, interview evidence, the audit
// trail — competes for one column of it. The drawer is deleted, not dormant.
//
// ─── THE LAYOUT ─────────────────────────────────────────────────────────────
//
// Two columns, from the very top of the page:
//
//   MIDDLE   who they are, then four tabs of evidence
//   RIGHT    the log, and nothing else
//
// The identity header is INSIDE the middle column rather than spanning both.
// Spanning, it pushed the log down by its own height, so the top of the right
// rail was blank and the trail began below the fold.
//
// The right rail is reserved for the audit trail on purpose. It is the one
// thing a reviewer reads WHILE reading something else — "was this person moved
// on before?" is a question you ask with the interview in front of you — so it
// stays put while the tabs change underneath it, and it scrolls on its own.
// Everything that was briefly parked there (the warm-up video, the integrity
// record) has moved into the AI interview tab, where that interview lives.
//
// ─── THE TABS ───────────────────────────────────────────────────────────────
//
// Stacked, these four sections ran to several screens of scrolling, and the
// question set — sixteen questions with model answers — sat below the fold
// under an interview nobody had done yet.
//
//   1. Profile          who they are: the established facts, and the
//                       structured read of the CV behind them. Opens here —
//                       it is the only tab that is never empty.
//   2. Resume analysis  what the screening found, requirement by requirement.
//   3. AI interview     the interview, its evidence, and the hire/reject
//                       decision.
//   4. Questions        what the AI asks, and what a good answer looks like.
//
// Order set by TABS, and FIRST_TAB follows it — reorder the array and the
// landing tab moves with it.

const FONT = "'Plus Jakarta Sans', 'Inter', sans-serif";

const T = {
  pageBg: "#f7f9fc",
  card: "#ffffff",
  cardLine: "#edf1f7",
  line: "#d0d5dd",
  ink: "#101828",
  strong: "#344054",
  body: "#475467",
  muted: "#667085",
  faint: "#98a2b3",
  wash: "#f9fafb",
  accent: "#0b8a8f",
  accentSoft: "#ecfdff",
  accentLine: "#c5eff1",
  good: "#067647",
  goodSoft: "#ecfdf3",
  goodLine: "#abefc6",
  danger: "#b42318",
  dangerSoft: "#fef3f2",
  dangerLine: "#fecdca",
  warnInk: "#b54708",
  warnSoft: "#fffaeb",
  warnLine: "#fedf89",
};

const VERDICT = {
  STRONG_YES: { label: "Strong yes", fg: T.good, bg: T.goodSoft, bd: T.goodLine },
  YES: { label: "Yes", fg: T.good, bg: T.goodSoft, bd: T.goodLine },
  BORDERLINE: { label: "Borderline", fg: T.warnInk, bg: T.warnSoft, bd: T.warnLine },
  NO: { label: "No", fg: T.danger, bg: T.dangerSoft, bd: T.dangerLine },
  STRONG_NO: { label: "Strong no", fg: T.danger, bg: T.dangerSoft, bd: T.dangerLine },
};

// The resume screening's three bands. Wording matters here: these describe the
// DOCUMENT, not the person, and the tooltips say so — a thin CV from a strong
// candidate lands in UNCLEAR, which is what the interview is for.
const BAND = {
  STRONG: {
    label: "Strong match",
    fg: T.good,
    bg: T.goodSoft,
    bd: T.goodLine,
    hint: "The resume evidences most of what the job asks for.",
  },
  POSSIBLE: {
    label: "Possible match",
    fg: T.warnInk,
    bg: T.warnSoft,
    bd: T.warnLine,
    hint: "Some requirements are evidenced, with gaps on the rest.",
  },
  UNCLEAR: {
    label: "Unclear",
    fg: T.muted,
    bg: T.wash,
    bd: T.cardLine,
    hint: "The resume does not say enough either way. Not a rejection.",
  },
};

// requirements[].met
const MET = {
  YES: { icon: CheckCircleRoundedIcon, color: T.good, label: "Evidenced" },
  NO: { icon: CancelRoundedIcon, color: T.danger, label: "Not evidenced" },
  UNCLEAR: { icon: HelpOutlineRoundedIcon, color: T.faint, label: "Resume does not say" },
};

const HIGHLIGHT_LABEL = {
  EMPLOYER: "Employers",
  PROJECT: "Projects",
  TOOL: "Tools & technologies",
  EDUCATION: "Education",
  CERTIFICATION: "Certifications",
};
// Fixed order, so the same candidate reads the same way twice.
const HIGHLIGHT_ORDER = ["EMPLOYER", "PROJECT", "TOOL", "EDUCATION", "CERTIFICATION"];

const TABS = [
  { key: "profile", label: "Profile" },
  { key: "resume", label: "Resume analysis" },
  { key: "interview", label: "AI interview" },
  { key: "questions", label: "Questions" },
];

// Which tab a freshly opened candidate lands on. Named rather than repeated:
// it is set in two places — initial state and the reset when the candidate
// changes — and those two disagreeing is a bug you only see on the second
// candidate you open.
const FIRST_TAB = TABS[0].key;

if (typeof document !== "undefined" && !document.getElementById("ats-candidate-page-ui")) {
  const style = document.createElement("style");
  style.id = "ats-candidate-page-ui";
  style.textContent = `
  @keyframes cpFade{ from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  .cp-page{ padding:14px 16px 32px; background:${T.pageBg}; min-height:100vh; font-family:${FONT}; }
  .cp-in{ animation:cpFade .35s ease both; }
  .cp-card{ background:${T.card}; border:1px solid ${T.cardLine}; border-radius:10px; box-shadow:none; }
  .cp-btn{
    height:36px; border-radius:9px !important; border:1px solid ${T.line} !important;
    background:${T.card} !important; color:${T.strong} !important; padding:0 14px !important;
    text-transform:none !important; font-weight:700 !important; font-size:13px !important;
    font-family:${FONT} !important; box-shadow:none !important; white-space:nowrap;
  }
  .cp-btn:hover{ border-color:${T.accent} !important; color:${T.accent} !important; }
  .cp-btn.Mui-disabled{ color:${T.faint} !important; border-color:${T.cardLine} !important; }
  .cp-fact{ display:inline-flex; align-items:center; gap:5px; font-size:12.5px; color:${T.body}; }
  .cp-secname{
    font-size:10.5px; font-weight:800; letter-spacing:.07em; text-transform:uppercase;
    color:${T.faint};
  }
  /* Two columns that become one on a narrow screen. The log goes BELOW the
     tabs when stacked — on a phone the evidence is what you came for, and a
     rail that jumps above it pushes the whole page down. */
  .cp-cols{ display:grid; grid-template-columns:minmax(0,1fr) 340px; gap:16px; align-items:start; }
  @media (max-width:1100px){ .cp-cols{ grid-template-columns:minmax(0,1fr); } }

  /* ── Each column scrolls on its own ──────────────────────────────────────
     The two sides are read against each other: you scroll to answer 14 in the
     middle and then check the trail on the right for whether this person has
     been moved on before. One shared scrollbar means reaching either end
     drags the other side away with it.
     So on a desktop the PAGE does not scroll — each column does. The height
     comes off the shell: ATSLayout is 100vh with overflow hidden, its Header
     is 60px, and its <main> adds 10px of padding top and bottom. Hence 80px.
     If the header's height ever changes, this number is what to change.
     Below 1101px the columns stack and this is all switched off — two
     independent scroll regions on a phone is a trap, so the page scrolls
     normally there. */
  @media (min-width:1101px){
    .cp-page{
      height:calc(100vh - 80px); min-height:0; overflow:hidden;
      display:flex; flex-direction:column; padding-bottom:14px;
    }
    .cp-cols{ flex:1 1 auto; min-height:0; align-items:stretch; }
    .cp-colscroll{
      min-height:0; height:100%; overflow-y:auto; overflow-x:hidden;
      /* Room for the scrollbar so it does not sit on top of the cards, and a
         little bottom padding so the last card is not flush to the edge. */
      padding-right:8px; padding-bottom:8px;
      overscroll-behavior:contain;
      scrollbar-width:thin; scrollbar-color:#dfe5ee transparent;
    }
    .cp-colscroll::-webkit-scrollbar{ width:8px }
    .cp-colscroll::-webkit-scrollbar-track{ background:transparent }
    .cp-colscroll::-webkit-scrollbar-thumb{ background:#dfe5ee; border-radius:8px }
    .cp-colscroll:hover::-webkit-scrollbar-thumb{ background:#cbd5e1 }
  }
  .cp-tabs .MuiTabs-flexContainer{ gap:2px; }
  .cp-tabs .MuiTab-root{
    text-transform:none; font-family:${FONT}; font-size:13px; font-weight:700;
    color:${T.muted}; min-height:44px; padding:0 14px; border-radius:8px 8px 0 0;
  }
  .cp-tabs .MuiTab-root:hover{ color:${T.strong}; background:${T.wash}; }
  .cp-tabs .Mui-selected{ color:${T.accent} !important; }
  .cp-tabs .MuiTabs-indicator{ height:2.5px; background:${T.accent}; border-radius:2px 2px 0 0; }
  `;
  document.head.appendChild(style);
}

// ── Small shared pieces ────────────────────────────────────────────────────

function Fact({ icon, children }) {
  if (!children) return null;
  return (
    <Box className="cp-fact">
      {icon}
      <span>{children}</span>
    </Box>
  );
}

function Section({ name, children, action, sx }) {
  return (
    <Box className="cp-card cp-in" sx={{ p: 2.25, ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
        <Typography className="cp-secname">{name}</Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function Pill({ fg, bg, bd, children, title }) {
  const pill = (
    <Box
      sx={{
        px: 1.25, py: "3px", borderRadius: "7px", fontSize: 11.5, fontWeight: 800,
        fontFamily: FONT, color: fg, bgcolor: bg, border: `1px solid ${bd}`,
        whiteSpace: "nowrap", display: "inline-block",
      }}
    >
      {children}
    </Box>
  );
  return title ? <Tooltip title={title}>{pill}</Tooltip> : pill;
}

// A verbatim pull from the CV. Quoted and indented so it reads as the
// document's words rather than the model's.
function Evidence({ children }) {
  if (!children) return null;
  return (
    <Typography
      sx={{
        fontFamily: FONT, fontSize: 12.5, color: T.muted, lineHeight: 1.6,
        borderLeft: `2px solid ${T.cardLine}`, pl: 1.15, mt: 0.4, fontStyle: "italic",
      }}
    >
      {children}
    </Typography>
  );
}

// An empty state that says WHY it is empty and what to do about it. Every one
// of these on the page follows the same shape, because "nothing here" with no
// explanation reads as a bug.
function Empty({ icon, title, children }) {
  return (
    <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
      {icon}
      <Box>
        <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: T.strong }}>
          {title}
        </Typography>
        {children && (
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: T.muted, mt: 0.25, lineHeight: 1.6 }}>
            {children}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// One label/value row in the Profile tab. Renders nothing when there is no
// value — an empty "Expected CTC" line is noise, not information.
function Row({ label, value, mono }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "170px minmax(0,1fr)",
        gap: 1.5,
        py: 0.85,
        borderBottom: `1px solid ${T.cardLine}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: T.faint, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : FONT,
          fontSize: 13.5, color: T.strong, fontWeight: 600, wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// `experience` is free text because "6 months" and "fresher" are real answers,
// but it is usually a bare number like "2", which reads as nothing at all next
// to a location and a phone number. A unit is added only when the value is
// purely digits, so "2.5 yrs" and "fresher" both come out right.
function yearsLabel(value) {
  if (value === null || value === undefined || value === "") return null;
  const s = String(value).trim();
  return /^\d+(\.\d+)?$/.test(s) ? `${s} yrs` : s;
}

function fmtDate(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function fmtMoney(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `₹${n.toLocaleString("en-IN")}`;
}

// A score the API reports as a DECIMAL STRING. "0.00" is what an unscored
// application carries, so `!= null` is true for everybody and a plain render
// puts a real-looking zero next to a resume nobody has read. Only above zero is
// a score.
function scoreOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function CandidateProfile() {
  const { application_id } = useParams();
  const navigate = useNavigate();
  // The pipeline opens this page in a NEW browser tab, where there is nothing
  // behind it — navigate(-1) there does nothing at all, or leaves the app. The
  // router numbers its entries in history.state.idx, and 0 means this page is
  // the first thing in the tab, so Back goes to the job (or the job list)
  // instead. Arriving the old way, from inside the app, it is still a real Back.
  const goBack = (fallback) => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(fallback);
  };

  const [app, setApp] = useState({ loading: true, error: null, data: null });
  // The interview is OPTIONAL, and its absence is not an error — most
  // candidates in a pipeline have not interviewed. Held separately so a 404
  // here cannot take the page down with it.
  const [review, setReview] = useState({ loading: true, data: null, missing: false });
  const [decision, setDecision] = useState({ open: null, reason: "", saving: false });
  const [notice, setNotice] = useState(null);
  const [tab, setTab] = useState(FIRST_TAB);
  // Whether the introduction is open, played from the avatar.
  const [playIntro, setPlayIntro] = useState(false);

  const loadApp = useCallback(async () => {
    setApp((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await applicationService.getApplicationById(application_id);
      const body = res?.data;
      const d = body?.data || null;

      // ── The identity key is `application_id`, not `id` ─────────────────
      //
      // This endpoint returns the application flattened, and it names the
      // primary key `application_id`. Checking `d.id` was wrong, and wrong in
      // the worst available way: the guard fired on a perfectly good response
      // and threw with the API's own `statusMessage`, so the page rendered a
      // RED error alert reading "Application fetched successfully". A success
      // message in an error box, and nothing else on screen.
      //
      // `success` is checked first, because this API reports failure in the
      // BODY with HTTP 200 — axios does not throw for it, so without this a
      // real 404 would arrive as `data: undefined` and blank the render.
      if (body && body.success === false) {
        throw new Error(body.statusMessage || "Could not load this candidate.");
      }
      if (!d || !(d.application_id || d.id)) {
        // Deliberately NOT statusMessage here. If we got this far the request
        // succeeded, so its message says "fetched successfully" and putting it
        // in an error box is how the last version lied about what happened.
        throw new Error("This candidate could not be found.");
      }
      setApp({ loading: false, error: null, data: d });
    } catch (e) {
      setApp({
        loading: false,
        error: e?.response?.data?.statusMessage || e?.message || "Could not load this candidate.",
        data: null,
      });
    }
  }, [application_id]);

  const loadReview = useCallback(async () => {
    setReview({ loading: true, data: null, missing: false });
    try {
      const res = await interviewService.review(application_id);
      const body = res?.data;
      if (!body?.success) {
        // 404-in-a-200-body is this API's house style. "No interview" is the
        // expected case, not a failure to report.
        setReview({ loading: false, data: null, missing: true });
        return;
      }
      setReview({ loading: false, data: body.data, missing: false });
    } catch {
      setReview({ loading: false, data: null, missing: true });
    }
  }, [application_id]);

  useEffect(() => {
    loadApp();
    loadReview();
  }, [loadApp, loadReview]);

  // Back to the first tab when the candidate changes.
  //
  // A route param change does NOT remount this component, so without it the
  // next candidate opens on whichever tab the last one was left on — and an
  // in-progress reject reason would carry across to a different person, which
  // is the dangerous half.
  useEffect(() => {
    setTab(FIRST_TAB);
    setPlayIntro(false);
    setDecision({ open: null, reason: "", saving: false });
    setNotice(null);
  }, [application_id]);

  const d = app.data;
  const session = review.data?.session || null;
  const verdict = session?.verdict ? VERDICT[session.verdict] : null;

  // The interview's own strengths/concerns are arrays of STRINGS. The resume
  // screening's are arrays of { point, evidence }. Different shapes, similar
  // names — kept apart deliberately.
  const sessionStrengths = useMemo(() => session?.score_breakdown?.strengths || [], [session]);
  const sessionConcerns = useMemo(() => session?.score_breakdown?.concerns || [], [session]);

  const rs = d?.resume_screening || null;

  const highlights = useMemo(() => {
    const groups = new Map();
    for (const h of rs?.highlights || []) {
      if (!h?.detail) continue;
      const kind = HIGHLIGHT_ORDER.includes(h.kind) ? h.kind : "TOOL";
      if (!groups.has(kind)) groups.set(kind, []);
      groups.get(kind).push(h.detail);
    }
    return HIGHLIGHT_ORDER.filter((k) => groups.has(k)).map((k) => [k, groups.get(k)]);
  }, [rs]);

  // The introduction video is the WARMUP answer — the one question that is
  // asked of everybody and scored for nobody. Finding it here rather than
  // making the reviewer scroll to Q1 is the whole reason it is on this page.
  const intro = useMemo(() => {
    const items = review.data?.items || [];
    return items.find((i) => i.category === "WARMUP" && i.video_url) || null;
  }, [review.data]);

  const submitDecision = async () => {
    const reason = decision.reason.trim();
    if (!reason) {
      setNotice({ severity: "warning", message: "Please say why. It is the only record of this decision." });
      return;
    }
    setDecision((s) => ({ ...s, saving: true }));
    try {
      const res = await interviewService.decide(application_id, decision.open, reason);
      if (!res.data?.success) throw new Error(res.data?.statusMessage || "Could not record the decision.");
      setNotice({ severity: "success", message: res.data.statusMessage || "Decision recorded." });
      setDecision({ open: null, reason: "", saving: false });
      await Promise.all([loadApp(), loadReview()]);
    } catch (e) {
      setNotice({
        severity: "error",
        message: e?.response?.data?.statusMessage || e?.message || "Could not record the decision.",
      });
      setDecision((s) => ({ ...s, saving: false }));
    }
  };

  // ── Loading / error ──────────────────────────────────────────────────────
  if (app.loading && !d) {
    return (
      // Same skeleton SHAPE as the loaded page — header inside the middle
      // column, log on the right. A skeleton laid out differently from what
      // replaces it makes the whole page jump at the moment the data lands.
      <Box className="cp-page">
        <Box className="cp-cols">
          <Box sx={{ minWidth: 0 }}>
            <Box className="cp-card" sx={{ p: 2.5, mb: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Skeleton variant="circular" width={76} height={76} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={220} height={30} />
                  <Skeleton variant="text" width={300} height={18} />
                  <Skeleton variant="text" width={420} height={18} />
                </Box>
              </Box>
            </Box>
            <Box className="cp-card" sx={{ p: 2.5 }}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="text" height={26} sx={{ mb: 0.5 }} />
              ))}
            </Box>
          </Box>
          <Box className="cp-card" sx={{ p: 2.5 }}>
            <Skeleton variant="rectangular" height={170} sx={{ borderRadius: 2 }} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (app.error) {
    return (
      <Box className="cp-page">
        <Button className="cp-btn" startIcon={<ArrowBackRoundedIcon />} onClick={() => goBack("/ats")} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error" sx={{ borderRadius: "10px", fontFamily: FONT }}>
          {app.error}
        </Alert>
      </Box>
    );
  }

  const cand = d?.candidate || {};
  const job = d?.job || null;
  // `job_id` is not a top-level field on this payload — the job arrives nested.
  // Reading d.job_id gave undefined, which quietly hid the "Back to job"
  // button and made the header's navigation a no-op.
  const jobId = job?.id || null;
  const jobTitle = job?.title || "";
  const stageName = d?.current_stage?.stage_name || "";
  const rejected = d?.status === "REJECTED";
  const questions = d?.screening_questions || [];
  const band = rs?.band ? BAND[rs.band] : null;

  // What the resume claimed against what a human established. Only worth
  // showing when they actually disagree by more than rounding.
  const claimedYears = rs?.years_experience;
  const recordedYears = Number(cand.experience);
  const yearsDisagree =
    claimedYears != null &&
    Number.isFinite(recordedYears) &&
    Math.abs(Number(claimedYears) - recordedYears) >= 1;

  return (
    <Box className="cp-page">
      {notice && (
        <Alert
          severity={notice.severity}
          onClose={() => setNotice(null)}
          sx={{ borderRadius: "10px", fontFamily: FONT, fontSize: 13, mb: 1.5 }}
        >
          {notice.message}
        </Alert>
      )}

      {/* The header is INSIDE the middle column, not above both.

          Full width, it pushed the log down by the height of the identity
          card — so the first thing on the right was empty space, and the
          trail started below the fold on a laptop. The log now begins at
          the top of the page, level with the name it belongs to. */}
      <Box className="cp-cols">
        {/* ── MIDDLE: who they are, then the evidence in tabs ─────────── */}
        <Box className="cp-colscroll" sx={{ minWidth: 0 }}>
          {/* ── Identity ────────────────────────────────────────────────────── */}
          <Box className="cp-card cp-in" sx={{ p: 2.25, mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
              <Tooltip title="Back">
                <Button
                  className="cp-btn"
                  onClick={() => goBack(jobId ? `/ats/job/${jobId}` : "/ats")}
                  sx={{ minWidth: 36, px: "0 !important", width: 36 }}
                >
                  <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                </Button>
              </Tooltip>

              {/* Their face, and a way into the introduction.
                  Three states, best first:
                    1. the device-check still — the photo the candidate
                       submitted before starting, and the only visual
                       confirmation that the person who sat the interview is
                       the person on the application;
                    2. a frame of their introduction video, which IS a
                       photograph of them once they have interviewed;
                    3. their initial, before either exists.
                  In state 1 or 2, clicking plays the introduction — the one
                  answer asked of everybody and scored for nobody, which makes
                  it the natural thing to reach for from a face. */}
              <Box
                className="cp-avatar"
                onClick={intro?.video_url ? () => setPlayIntro(true) : undefined}
                role={intro?.video_url ? "button" : undefined}
                tabIndex={intro?.video_url ? 0 : undefined}
                aria-label={intro?.video_url ? "Play introduction" : undefined}
                onKeyDown={
                  intro?.video_url
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setPlayIntro(true);
                        }
                      }
                    : undefined
                }
                sx={{
                  position: "relative",
                  width: 76,
                  height: 76,
                  borderRadius: "50%",
                  flexShrink: 0,
                  overflow: "hidden",
                  bgcolor: T.wash,
                  border: `1px solid ${T.cardLine}`,
                  display: "grid",
                  placeItems: "center",
                  cursor: intro?.video_url ? "pointer" : "default",
                  "&:focus-visible": { outline: `2px solid ${T.accent}`, outlineOffset: 2 },
                }}
              >
                {session?.id_photo_url ? (
                  <img
                    src={session.id_photo_url}
                    alt={cand.full_name || "candidate"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      // Faces sit high in a webcam frame; a centred crop of a
                      // circle keeps the ceiling and loses the chin.
                      objectPosition: "center 28%",
                    }}
                  />
                ) : intro?.video_url ? (
                  // `#t=0.6` rather than the first frame, which is the moment
                  // before they start talking and is usually black or a blink.
                  <video
                    src={`${intro.video_url}#t=0.6`}
                    preload="metadata"
                    muted
                    playsInline
                    tabIndex={-1}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center 28%",
                      display: "block",
                      pointerEvents: "none",
                    }}
                  />
                ) : (
                  <Typography sx={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, color: T.faint }}>
                    {String(cand.full_name || "?").trim().charAt(0).toUpperCase()}
                  </Typography>
                )}

                {/* The play affordance is HIDDEN. The avatar is a portrait
                    first — a badge sitting on someone's face permanently is a
                    control, not a photograph, and the header is where you look
                    to recognise a person rather than to operate something.
                    It fades in on hover and on keyboard focus so the
                    affordance is still discoverable, and the click target is
                    unchanged either way. */}
                {intro?.video_url && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "grid",
                      placeItems: "center",
                      opacity: 0,
                      transition: "opacity .18s ease, transform .15s ease",
                      ".cp-avatar:hover &": { opacity: 1, transform: "scale(1.06)" },
                      ".cp-avatar:focus-visible &": { opacity: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,.92)",
                        display: "grid",
                        placeItems: "center",
                        boxShadow: "0 1px 4px rgba(0,0,0,.35)",
                      }}
                    >
                      <PlayArrowRoundedIcon sx={{ fontSize: 17, color: T.ink }} />
                    </Box>
                  </Box>
                )}
              </Box>

              <Box sx={{ minWidth: 240, flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: 22,
                      fontWeight: 800,
                      color: T.ink,
                      letterSpacing: "-0.4px",
                      textDecoration: rejected ? "line-through" : "none",
                    }}
                  >
                    {cand.full_name || "(name not read from the resume)"}
                  </Typography>
                  {stageName && (
                    <Pill fg={T.accent} bg={T.accentSoft} bd={T.accentLine}>
                      {stageName}
                    </Pill>
                  )}
                  {rejected && (
                    <Pill fg={T.danger} bg={T.dangerSoft} bd={T.dangerLine}>
                      Rejected
                    </Pill>
                  )}
                  {/* The screening's headline verdict, in the header rather than
                      only inside its tab. It is the one thing a reviewer wants
                      before they have clicked anything. */}
                  {band && (
                    <Pill fg={band.fg} bg={band.bg} bd={band.bd} title={band.hint}>
                      {band.label}
                      {rs.fit_score != null ? ` · ${rs.fit_score}/10` : ""}
                    </Pill>
                  )}
                </Box>

                {jobTitle && (
                  <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: T.muted, fontStyle: "italic", mt: 0.2 }}>
                    {jobTitle}
                  </Typography>
                )}

                {/* The facts on one line. They were a stacked card in the drawer,
                    which is three rows to say what reads as a sentence. */}
                <Box sx={{ display: "flex", gap: 2.25, flexWrap: "wrap", rowGap: 0.75, mt: 1 }}>
                  <Fact icon={<WorkOutlineRoundedIcon sx={{ fontSize: 15, color: T.faint }} />}>
                    {yearsLabel(cand.experience) ||
                      (cand.experience_years != null ? `${cand.experience_years} yrs` : null)}
                  </Fact>
                  <Fact icon={<PlaceOutlinedIcon sx={{ fontSize: 15, color: T.faint }} />}>{cand.city}</Fact>
                  <Fact icon={<EventAvailableOutlinedIcon sx={{ fontSize: 15, color: T.faint }} />}>
                    {cand.notice_period_days != null ? `Notice: ${cand.notice_period_days} days` : null}
                  </Fact>
                  <Fact icon={<MailOutlineRoundedIcon sx={{ fontSize: 15, color: T.faint }} />}>{cand.email}</Fact>
                  <Fact icon={<CallOutlinedIcon sx={{ fontSize: 15, color: T.faint }} />}>{cand.phone}</Fact>
                </Box>

                <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                  <Button
                    className="cp-btn"
                    disabled={!cand.resume_url}
                    startIcon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 17 }} />}
                    onClick={() => cand.resume_url && window.open(cand.resume_url, "_blank", "noopener")}
                  >
                    {cand.resume_url ? "Open resume" : "No resume on file"}
                  </Button>
                  {jobId && (
                    <Button
                      className="cp-btn"
                      startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
                      onClick={() => navigate(`/ats/job/${jobId}`)}
                    >
                      Back to job
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className="cp-card" sx={{ px: 1, pt: 0.5, mb: 2, borderRadius: "10px" }}>
            <Tabs
              className="cp-tabs"
              value={tab}
              onChange={(_e, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {TABS.map((t) => (
                <Tab
                  key={t.key}
                  value={t.key}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {t.label}
                      {/* Counts and states on the tab itself, so the reviewer
                          knows what is behind it before paying a click. */}
                      {t.key === "questions" && questions.length > 0 && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 11, fontWeight: 800, color: T.faint,
                            bgcolor: T.wash, border: `1px solid ${T.cardLine}`,
                            borderRadius: "5px", px: 0.6, lineHeight: 1.7,
                          }}
                        >
                          {questions.length}
                        </Box>
                      )}
                      {t.key === "interview" && !review.loading && review.missing && (
                        <Box
                          component="span"
                          sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: T.faint }}
                        />
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* ══ 1. RESUME ANALYSIS ═══════════════════════════════════════ */}
          {tab === "resume" && (
            <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
              {!rs && (
                <Section name="Resume analysis">
                  {/* Why there is no analysis matters. "The PDF could not be
                      read" is somebody's job to fix; "not screened yet" fixes
                      itself on the next run. */}
                  {d?.parse_status === "NEEDS_MANUAL_REVIEW" || d?.parse_error ? (
                    <Empty
                      icon={<DescriptionOutlinedIcon sx={{ fontSize: 18, color: T.warnInk, mt: "1px" }} />}
                      title="This resume could not be read"
                    >
                      {d?.parse_error
                        ? `The parser reported: ${d.parse_error}`
                        : "The file needs a manual look — it may be a scan, or password protected."}{" "}
                      Open the resume above and record what matters on the
                      Profile tab.
                    </Empty>
                  ) : (
                    <Empty
                      icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: T.faint, mt: "1px" }} />}
                      title="Not screened for this job yet"
                    >
                      Run screening from the job&rsquo;s candidate list and the
                      band, fit score and requirement-by-requirement read will
                      appear here. A candidate screened for a different job does
                      not count — the analysis is always against one job&rsquo;s
                      requirements.
                    </Empty>
                  )}
                </Section>
              )}

              {rs && (
                <Section
                  name="What the screening found"
                  action={
                    band && (
                      <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                        {rs.fit_score != null && (
                          <Tooltip title="How well the RESUME matches this job, 1-10. It rates the document, not the person.">
                            <Box
                              sx={{
                                fontFamily: FONT, fontSize: 12, fontWeight: 800, color: T.strong,
                                bgcolor: T.wash, border: `1px solid ${T.cardLine}`,
                                borderRadius: "7px", px: 1, py: "3px",
                              }}
                            >
                              {rs.fit_score}/10
                            </Box>
                          </Tooltip>
                        )}
                        <Pill fg={band.fg} bg={band.bg} bd={band.bd} title={band.hint}>
                          {band.label}
                        </Pill>
                      </Box>
                    )
                  }
                >
                  {rs.band_reason && (
                    <Box
                      sx={{
                        bgcolor: band ? band.bg : T.wash,
                        border: `1px solid ${band ? band.bd : T.cardLine}`,
                        borderRadius: "8px", px: 1.5, py: 1.15, mb: rs.summary ? 1.5 : 0,
                      }}
                    >
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: band ? band.fg : T.strong, lineHeight: 1.6 }}>
                        {rs.band_reason}
                      </Typography>
                    </Box>
                  )}
                  {rs.summary && (
                    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: T.body, lineHeight: 1.75, whiteSpace: "pre-wrap" }}>
                      {rs.summary}
                    </Typography>
                  )}
                </Section>
              )}

              {/* The requirement-by-requirement read. The most useful thing on
                  the page and the hardest to reconstruct by eye from a CV. */}
              {rs?.requirements?.length > 0 && (
                <Section name={`Against the job's requirements · ${rs.requirements.length}`}>
                  <Box sx={{ display: "grid", gap: 1.4 }}>
                    {rs.requirements.map((r, i) => {
                      const m = MET[r.met] || MET.UNCLEAR;
                      const Icon = m.icon;
                      return (
                        <Box key={i} sx={{ display: "flex", gap: 1.15, alignItems: "flex-start" }}>
                          <Tooltip title={m.label}>
                            <Icon sx={{ fontSize: 17, color: m.color, flexShrink: 0, mt: "2px" }} />
                          </Tooltip>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: T.strong, lineHeight: 1.55 }}>
                              {r.requirement}
                            </Typography>
                            <Evidence>{r.evidence}</Evidence>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Section>
              )}

              {/* Strengths and concerns, each with the line from the CV that
                  justifies it. Side by side on a wide screen: they are read
                  against each other, not in sequence. */}
              {(rs?.strengths?.length > 0 || rs?.concerns?.length > 0) && (
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "minmax(0,1fr)", md: "repeat(2, minmax(0,1fr))" },
                  }}
                >
                  {rs.strengths?.length > 0 && (
                    <Section name={`Strengths · ${rs.strengths.length}`}>
                      <Box sx={{ display: "grid", gap: 1.3 }}>
                        {rs.strengths.map((s, i) => (
                          <Box key={i} sx={{ display: "flex", gap: 1.15, alignItems: "flex-start" }}>
                            <CheckCircleRoundedIcon sx={{ fontSize: 17, color: T.good, flexShrink: 0, mt: "2px" }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: T.strong, lineHeight: 1.55 }}>
                                {s.point}
                              </Typography>
                              <Evidence>{s.evidence}</Evidence>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Section>
                  )}

                  {rs.concerns?.length > 0 && (
                    <Section name={`Concerns · ${rs.concerns.length}`}>
                      <Box sx={{ display: "grid", gap: 1.3 }}>
                        {rs.concerns.map((c, i) => (
                          <Box key={i} sx={{ display: "flex", gap: 1.15, alignItems: "flex-start" }}>
                            <CancelRoundedIcon sx={{ fontSize: 17, color: T.danger, flexShrink: 0, mt: "2px" }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: T.strong, lineHeight: 1.55 }}>
                                {c.point}
                              </Typography>
                              <Evidence>{c.evidence}</Evidence>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Section>
                  )}
                </Box>
              )}

              {/* A human's own screening note, when one was recorded. Separate
                  from everything above, which is the model's output. */}
              {(scoreOrNull(d?.screening_score) !== null || d?.remarks) && (
                <Section name="Recorded by a reviewer">
                  {scoreOrNull(d?.screening_score) !== null && (
                    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: T.body, mb: d.remarks ? 1 : 0 }}>
                      Screening score: <strong>{scoreOrNull(d.screening_score)}</strong>
                    </Typography>
                  )}
                  {d.remarks && (
                    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: T.body, whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                      {d.remarks}
                    </Typography>
                  )}
                </Section>
              )}
            </Box>
          )}

          {/* ══ 2. INTERVIEW ANALYSIS ════════════════════════════════════ */}
          {tab === "interview" && (
            <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
              {/* Outcome first, but only when there IS an outcome. An empty
                  "Hiring Decision" card above a candidate who has not
                  interviewed invites a decision with nothing behind it. */}
              {session && (
                <Section
                  name="Interview outcome & hiring decision"
                  action={verdict && <Pill fg={verdict.fg} bg={verdict.bg} bd={verdict.bd}>{verdict.label}</Pill>}
                >
                  {session.verdict_reason && (
                    <Box
                      sx={{
                        bgcolor: T.warnSoft,
                        border: `1px solid ${T.warnLine}`,
                        borderRadius: "8px",
                        px: 1.5, py: 1.15, mb: 1.75,
                      }}
                    >
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: T.warnInk }}>
                        Recommendation: {session.verdict_reason}
                      </Typography>
                    </Box>
                  )}

                  {/* What went well and what did not, as the model found it. */}
                  {(sessionStrengths.length > 0 || sessionConcerns.length > 0) && (
                    <Box sx={{ display: "grid", gap: 0.85, mb: 2 }}>
                      {sessionStrengths.map((t, i) => (
                        <Box key={`s${i}`} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                          <CheckCircleRoundedIcon sx={{ fontSize: 17, color: T.good, flexShrink: 0, mt: "1px" }} />
                          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: T.body, lineHeight: 1.55 }}>
                            {t}
                          </Typography>
                        </Box>
                      ))}
                      {sessionConcerns.map((t, i) => (
                        <Box key={`c${i}`} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                          <CancelRoundedIcon sx={{ fontSize: 17, color: T.danger, flexShrink: 0, mt: "1px" }} />
                          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: T.body, lineHeight: 1.55 }}>
                            {t}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* The decision. A reason is compulsory — the same rule the
                      rejection dialog enforces, for the same reason: six months
                      on it is the only thing that answers "why did we say no". */}
                  {decision.open ? (
                    <Box>
                      <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        value={decision.reason}
                        onChange={(e) => setDecision((s) => ({ ...s, reason: e.target.value }))}
                        label={decision.open === "ADVANCE" ? "Why are you selecting them?" : "Why are you rejecting them?"}
                        placeholder="Recorded against your name on this candidate's history."
                        disabled={decision.saving}
                        sx={{ mb: 1.25, "& .MuiOutlinedInput-root": { borderRadius: "9px", fontSize: 13.5 } }}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          onClick={submitDecision}
                          disabled={decision.saving || !decision.reason.trim()}
                          sx={{
                            textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: "9px",
                            px: 2.25, height: 36, color: "#fff",
                            bgcolor: decision.open === "ADVANCE" ? T.good : T.danger,
                            "&:hover": { bgcolor: decision.open === "ADVANCE" ? "#05613a" : "#912018" },
                          }}
                          startIcon={decision.saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
                        >
                          {decision.saving
                            ? "Saving…"
                            : decision.open === "ADVANCE"
                              ? "Confirm select"
                              : "Confirm reject"}
                        </Button>
                        <Button
                          className="cp-btn"
                          onClick={() => setDecision({ open: null, reason: "", saving: false })}
                          disabled={decision.saving}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        onClick={() => setDecision({ open: "ADVANCE", reason: "", saving: false })}
                        sx={{
                          textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: "9px",
                          px: 2.5, height: 36, color: "#fff", bgcolor: T.good,
                          "&:hover": { bgcolor: "#05613a" },
                        }}
                      >
                        Select
                      </Button>
                      <Button
                        onClick={() => setDecision({ open: "REJECT", reason: "", saving: false })}
                        sx={{
                          textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: "9px",
                          px: 2.5, height: 36, color: T.danger,
                          border: `1px solid ${T.dangerLine}`, bgcolor: T.card,
                          "&:hover": { bgcolor: T.dangerSoft },
                        }}
                      >
                        Reject
                      </Button>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: T.faint, alignSelf: "center" }}>
                        Marks this round passed or failed. It does not move them to another round.
                      </Typography>
                    </Box>
                  )}
                </Section>
              )}

              {/* The warm-up answer. Asked of everybody, scored for nobody, and
                  usually the first thing a reviewer plays. It was in the right
                  rail; that rail is the log now, so it sits with the rest of
                  its own interview. Capped in width because a full-column
                  video is all anyone would see. */}
              {intro && (
                <Section name="Introduction">
                  {/* Same shape as every answer below: the words take the
                      column, the recording takes a thumbnail. It is also
                      reachable from the avatar in the header, which is where
                      most people will click it. */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, order: { xs: 2, sm: 1 } }}>
                      {intro.transcript ? (
                        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: T.body, lineHeight: 1.7 }}>
                          {intro.transcript}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: T.muted, fontStyle: "italic" }}>
                          No transcript for the introduction.
                        </Typography>
                      )}
                    </Box>
                    <VideoThumb
                      src={intro.video_url}
                      onClick={() => setPlayIntro(true)}
                      badge={
                        intro.duration_ms
                          ? `${Math.round(intro.duration_ms / 1000)}s · play`
                          : "play"
                      }
                      sx={{ order: { xs: 1, sm: 2 } }}
                    />
                  </Box>
                </Section>
              )}

              {review.loading && (
                <Box className="cp-card" sx={{ p: 2.25 }}>
                  <Skeleton variant="text" width={180} height={22} />
                  <Skeleton variant="text" height={20} />
                  <Skeleton variant="text" height={20} width="70%" />
                </Box>
              )}

              {/* The interview itself. Shared with the review console so the
                  evidence behind a decision is rendered once. */}
              {!review.loading && session && <InterviewReview data={review.data} />}

              {!review.loading && session && (
                <Section
                  name="Integrity record"
                  action={<ShieldOutlinedIcon sx={{ fontSize: 17, color: T.faint }} />}
                >
                  {/* Facts, with their innocent explanations attached. An
                      integrity panel that reports a number without saying what
                      it usually means is an accusation dressed as data. */}
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: T.body }}>
                    {session.integrity_events?.length
                      ? `${session.integrity_events.length} focus event${session.integrity_events.length === 1 ? "" : "s"} — the candidate switched away from the tab or window.`
                      : "No focus events recorded."}
                  </Typography>
                  {Boolean(session.integrity_events?.length) && (
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: T.faint, mt: 0.5 }}>
                      Common, and usually innocent — a notification, a dropped
                      connection, or re-reading the question.
                    </Typography>
                  )}
                  <Box sx={{ height: 1, bgcolor: T.cardLine, my: 1.25 }} />
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: T.muted }}>
                    {session.device_check
                      ? `Camera ${session.device_check.camera_ok ? "confirmed" : "not confirmed"} · microphone ${session.device_check.mic_ok ? "confirmed" : "not confirmed"}`
                      : "No device check recorded."}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: T.muted, mt: 0.4 }}>
                    Attempt {session.attempts_used} of {session.max_attempts}
                  </Typography>
                  {!intro && (
                    <>
                      <Box sx={{ height: 1, bgcolor: T.cardLine, my: 1.25 }} />
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <VideocamOutlinedIcon sx={{ fontSize: 17, color: T.faint }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: T.muted }}>
                          No warm-up recording for this interview.
                        </Typography>
                      </Box>
                    </>
                  )}
                  {session.status !== "SCORED" && (
                    <Alert severity="info" sx={{ mt: 1.25, borderRadius: "8px", fontSize: 12 }}>
                      This interview is {String(session.status || "").toLowerCase()} — not scored yet.
                    </Alert>
                  )}
                </Section>
              )}

              {!review.loading && review.missing && (
                <Section name="Interview">
                  <Empty
                    icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: T.faint, mt: "1px" }} />}
                    title="No interview yet"
                  >
                    Send this candidate the interview link from the AI/Call
                    Interview round on the job&rsquo;s candidate list. Their
                    answers, per-answer ratings and recordings will appear here.
                  </Empty>
                </Section>
              )}
            </Box>
          )}

          {/* ══ 3. PROFILE ═══════════════════════════════════════════════ */}
          {tab === "profile" && (
            <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
              <Section name="Established facts">
                {/* What a human recorded, which outranks anything read off the
                    CV. The AI gets name, email and phone wrong often enough
                    that these are editable on the candidate list. */}
                <Box>
                  <Row label="Name" value={cand.full_name} />
                  <Row label="Email" value={cand.email} />
                  <Row label="Phone" value={cand.phone} />
                  <Row label="City" value={cand.city} />
                  <Row label="Current company" value={cand.current_company} />
                  <Row label="Experience" value={yearsLabel(cand.experience)} />
                  <Row
                    label="Notice period"
                    value={cand.notice_period_days != null ? `${cand.notice_period_days} days` : null}
                  />
                  <Row label="Expected CTC (per year)" value={fmtMoney(cand.expected_salary_annual)} />
                  <Row label="Source" value={cand.source} />
                  <Row label="Applied on" value={fmtDate(d?.applied_at)} />
                  <Row label="Round" value={stageName} />
                  <Row label="Status" value={d?.status} />
                  <Row
                    label="LinkedIn"
                    value={
                      cand.linkedin_url ? (
                        <a
                          href={cand.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: T.accent, fontWeight: 700, textDecoration: "none" }}
                        >
                          Open profile
                        </a>
                      ) : null
                    }
                  />
                </Box>
              </Section>

              {/* The resume said one thing and somebody established another.
                  Not an accusation — CVs go stale — but the reviewer should
                  know before they quote a number in an offer. */}
              {yearsDisagree && (
                <Section name="Worth checking">
                  <Box sx={{ display: "flex", gap: 1.15, alignItems: "flex-start" }}>
                    <HelpOutlineRoundedIcon sx={{ fontSize: 18, color: T.warnInk, mt: "1px", flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: T.body, lineHeight: 1.7 }}>
                      The resume reads as about <strong>{claimedYears} years</strong> of
                      experience, but <strong>{yearsLabel(cand.experience)}</strong> is
                      what is recorded here. Worth confirming which is right —
                      the recorded figure is what a person was told, so it wins
                      unless somebody changes it.
                    </Typography>
                  </Box>
                </Section>
              )}

              {/* The structured read of the CV. The same pulls the interview
                  uses to personalise its questions, which is why they are
                  worth seeing: they are what the candidate will be asked
                  about. */}
              {highlights.length > 0 && (
                <Section name="What the resume says">
                  <Box sx={{ display: "grid", gap: 1.75 }}>
                    {highlights.map(([kind, items]) => (
                      <Box key={kind}>
                        <Typography
                          sx={{
                            fontFamily: FONT, fontSize: 11, fontWeight: 800, color: T.faint,
                            letterSpacing: ".04em", textTransform: "uppercase", mb: 0.7,
                          }}
                        >
                          {HIGHLIGHT_LABEL[kind] || kind}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
                          {items.map((text, i) => (
                            <Box
                              key={i}
                              sx={{
                                fontFamily: FONT, fontSize: 12.5, color: T.strong, fontWeight: 600,
                                bgcolor: T.wash, border: `1px solid ${T.cardLine}`,
                                borderRadius: "7px", px: 1, py: 0.5, lineHeight: 1.5,
                              }}
                            >
                              {text}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Section>
              )}

              {/* Scores across the whole pipeline, and only the ones that
                  exist. Every one of these columns defaults to "0.00", so an
                  unfiltered render invents four zeroes for a candidate who has
                  done nothing but apply. */}
              {[
                ["Screening", d?.screening_score],
                ["Interview", d?.interview_score],
                ["Assessment", d?.assessment_score],
                ["Final", d?.final_score],
              ].some(([, v]) => scoreOrNull(v) !== null) && (
                <Section name="Scores">
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {[
                      ["Screening", d?.screening_score],
                      ["Interview", d?.interview_score],
                      ["Assessment", d?.assessment_score],
                      ["Final", d?.final_score],
                    ].map(([label, value]) => {
                      const n = scoreOrNull(value);
                      if (n === null) return null;
                      return (
                        <Box
                          key={label}
                          sx={{
                            minWidth: 96, px: 1.5, py: 1, borderRadius: "9px",
                            bgcolor: T.wash, border: `1px solid ${T.cardLine}`,
                          }}
                        >
                          <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: T.faint }}>
                            {label}
                          </Typography>
                          <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: T.ink }}>
                            {n}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Section>
              )}
            </Box>
          )}

          {/* ══ 4. QUESTIONS ═════════════════════════════════════════════ */}
          {tab === "questions" && (
            <ScreeningQuestions questions={questions} jobTitle={jobTitle} />
          )}
        </Box>

        {/* ── RIGHT: the log, and nothing else ──────────────────────────── */}
        <Box
          className="cp-rail cp-colscroll"
          sx={{ display: "grid", gap: 2, minWidth: 0, alignContent: "start" }}
        >
          <Box className="cp-card cp-in" sx={{ p: 2.25 }}>
            <CandidateHistory
              applicationId={Number(application_id)}
              candidateName={cand.full_name}
              onChanged={loadApp}
            />
          </Box>
        </Box>
      </Box>

      {/* The introduction, played from the avatar or from its own card. */}
      <Dialog
        open={playIntro && Boolean(intro?.video_url)}
        onClose={() => setPlayIntro(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogContent sx={{ p: 0, bgcolor: "#0c0a09", position: "relative", lineHeight: 0 }}>
          <IconButton
            onClick={() => setPlayIntro(false)}
            aria-label="Close"
            sx={{
              position: "absolute", top: 8, right: 8, zIndex: 2, color: "#fff",
              bgcolor: "rgba(0,0,0,.45)", "&:hover": { bgcolor: "rgba(0,0,0,.65)" },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
          {intro?.video_url && (
            <video
              src={intro.video_url}
              controls
              autoPlay
              style={{ width: "100%", maxHeight: "70vh", display: "block", background: "#0c0a09" }}
            />
          )}
        </DialogContent>
        <Box sx={{ px: 2.25, py: 1.75 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: T.ink }}>
            {cand.full_name || "Introduction"}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: T.muted, mt: 0.25 }}>
            The warm-up answer &mdash; asked of every candidate, scored for none.
          </Typography>
        </Box>
      </Dialog>
    </Box>
  );
}
